import { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../components/AppLayout';
import { listInsuranceRemovalRequests } from '../../services/reportService';

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString();
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const PRESETS = [
  { label: 'Today', from: todayStr(), to: todayStr() },
  { label: 'Last 7 days', from: daysAgoStr(6), to: todayStr() },
  { label: 'Last 30 days', from: daysAgoStr(29), to: todayStr() },
  { label: 'Last 90 days', from: daysAgoStr(89), to: todayStr() },
];

function SummaryCard({ label, value, accent }) {
  return (
    <div className="panel" style={{ padding: '18px 20px' }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 900, color: accent ? 'var(--accent)' : 'var(--text-primary)' }}>
        {value}
      </div>
    </div>
  );
}

export default function InsuranceRemovalRequestsReport() {
  const [filters, setFilters] = useState({
    from: daysAgoStr(29),
    to: todayStr(),
    driverId: '',
    limit: '1000',
  });
  const [submitted, setSubmitted] = useState({ ...filters });
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasRun, setHasRun] = useState(false);

  const runReport = useCallback(async (params) => {
    setLoading(true);
    setError('');
    try {
      const query = { limit: params.limit || 1000 };
      if (params.from) query.from = params.from;
      if (params.to) query.to = params.to;
      if (params.driverId?.trim()) query.driverId = params.driverId.trim();
      const res = await listInsuranceRemovalRequests(query);
      setRequests(res.data.requests || []);
      setTotal(res.data.total ?? res.data.requests?.length ?? 0);
      setHasRun(true);
    } catch {
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Run on mount with default filters
  useEffect(() => { runReport(submitted); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted({ ...filters });
    runReport(filters);
  };

  const applyPreset = (p) => {
    const next = { ...filters, from: p.from, to: p.to };
    setFilters(next);
    setSubmitted(next);
    runReport(next);
  };

  const exportCsv = () => {
    const headers = ['Driver ID', 'Driver Name', 'Cab #', 'Email', 'Sent Date', 'Effective Date'];
    const rows = requests.map((r) => [
      r.driverId,
      r.driverName,
      r.cabNumber,
      r.email,
      r.sentAt ? new Date(r.sentAt).toISOString() : '',
      r.effectiveDate ? new Date(r.effectiveDate).toISOString() : '',
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `insurance-removal-requests-${submitted.from}-to-${submitted.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AppLayout
      title="Insurance Removal Requests"
      subtitle="Driver-submitted requests to be removed from the company insurance policy."
    >
      {/* ── Filters ───────────────────────────────────── */}
      <div className="surface" style={{ marginBottom: 24 }}>
        <form onSubmit={handleSubmit}>
          <div className="toolbar" style={{ marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            {PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className="btn btn-ghost"
                style={{ fontSize: '0.8rem' }}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="form-grid" style={{ maxWidth: 700 }}>
            <div>
              <label htmlFor="irr-from">From</label>
              <input
                id="irr-from"
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((p) => ({ ...p, from: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="irr-to">To</label>
              <input
                id="irr-to"
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((p) => ({ ...p, to: e.target.value }))}
                required
              />
            </div>
            <div>
              <label htmlFor="irr-driver">
                Driver ID <span style={{ color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <input
                id="irr-driver"
                type="text"
                value={filters.driverId}
                onChange={(e) => setFilters((p) => ({ ...p, driverId: e.target.value }))}
                placeholder="Filter by driver"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Loading…' : 'Run report'}
              </button>
              {hasRun && requests.length > 0 && (
                <button type="button" className="btn btn-ghost" onClick={exportCsv}>
                  Export CSV
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {error && (
        <div className="feedback error" style={{ marginBottom: 16 }}>
          {error}{' '}
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: '0.8rem', marginLeft: 8 }}
            onClick={() => runReport(submitted)}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Summary cards ─────────────────────────────── */}
      {hasRun && !loading && requests.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <SummaryCard label="Total requests" value={total.toLocaleString()} accent />
        </div>
      )}

      {/* ── Table ─────────────────────────────────────── */}
      {hasRun && (
        <div className="surface">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <h3 style={{ margin: 0 }}>Requests</h3>
            {!loading && (
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                {total.toLocaleString()} record{total !== 1 ? 's' : ''}
                {requests.length < total ? ` (showing first ${requests.length})` : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 12 }} />
          ) : requests.length === 0 ? (
            <div className="empty-state">No records found for the selected filters.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver ID</th>
                    <th>Name</th>
                    <th>Cab #</th>
                    <th>Email</th>
                    <th>Sent</th>
                    <th>Effective date</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r._id}>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.driverId}</span>
                      </td>
                      <td>{r.driverName || '—'}</td>
                      <td>{r.cabNumber || '—'}</td>
                      <td>{r.email || '—'}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{fmtDateTime(r.sentAt)}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{fmtDate(r.effectiveDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
